import type { HubFaq, HubSection } from "@/components/HubEditorial";

/**
 * Fördjupning till fas 3-guiderna.
 *
 * Syftet är att varje sida ska kunna besvara hela sökintentionen på egen hand:
 * direktsvar högst upp (det AI-sökmotorer citerar), jämförelsetabeller med
 * faktiska siffror från vår verkstad i Kalix, beslutsunderlag och fler FAQ.
 */

export const GUIDE_AUTHOR = "AK Husbil & Fritid, verkstaden i Kalix";
export const GUIDE_UPDATED = "augusti 2026";

/* -------------------------- /dieselvarmare-husbil ------------------------- */

export const dieselvarmareTldr: string[] = [
  "En 4 kW dieselvärmare drar 0,20–0,45 l/h — i praktiken 2,1 l/dygn vid −5 °C, 3,8 l vid −15 °C och 5,4 l vid −25 °C.",
  "Färdigmonterad kostar 2 kW 18 000–24 000 kr, 4 kW 22 000–30 000 kr och ett vattenburet system 45 000–70 000 kr.",
  "Räkna med 25–50 Ah per dygn ur batteribanken — vinterdrift utan landström kräver litium eller solceller.",
  "Motionera värmaren på full effekt 20 minuter i månaden året runt; det förebygger de flesta startfel.",
  "Under sex meter räcker 2 kW, 7–8 meter tar 4 kW, över 8 meter eller under −25 °C behövs 5–6 kW.",
];

export const dieselvarmareExtraSections: HubSection[] = [
  {
    h2: "Modelljämförelse — vad du faktiskt väljer mellan",
    paragraphs: [
      "Fyra system dominerar den svenska marknaden och vi monterar och servar alla fyra. Skillnaderna handlar mindre om värmeeffekt och mer om ljudnivå, servicebarhet och hur lätt det är att få reservdelar när du står i Kiruna en söndag i januari.",
      "Webasto Air Top 2000 STC är den vanligaste luftvärmaren i campervans: låg strömförbrukning, tyst i låg effekt och reservdelar hos nästan varje lastbilsverkstad i landet. Eberspächer Airtronic S4 är den vi väljer när kunden kör mycket vid riktigt låga temperaturer — den håller effekten bättre nedåt mot −30 °C. Truma Combi D är ett smart val när bilen redan har Truma-kanaler dragna, eftersom hela installationen då blir en fråga om att byta enhet. Alde med dieselpanna är dyrast och tystast, och det enda system som ger både golvvärme, tappvarmvatten och motorförvärmning.",
      "Kinesiska kopior för 3 000–5 000 kr fungerar ofta första säsongen, men saknar CE-dokumentation för fast installation i fordon, har ingen reservdelskedja och godkänns inte av alla försäkringsbolag vid brand. Vi monterar dem inte, och vi rekommenderar att du inte gör det heller i en bil som är värd sexsiffrigt.",
    ],
    table: {
      caption: "Fyra vanliga system, priser färdigmonterade hos oss 2026",
      head: ["System", "Effekt", "Pris monterad", "Passar bäst"],
      rows: [
        ["Webasto Air Top 2000 STC", "2 kW luft", "18 000–24 000 kr", "Campervan under 6 m"],
        ["Eberspächer Airtronic S4", "4 kW luft", "22 000–30 000 kr", "Halvintegrerad, hård vinter"],
        ["Truma Combi D 6", "6 kW luft + vatten", "32 000–42 000 kr", "Bil med befintliga Truma-kanaler"],
        ["Alde med dieselpanna", "Vattenburet", "45 000–70 000 kr", "Integrerad, helårsboende"],
      ],
    },
    bullets: [
      "Välj efter reservdelstillgång och ljudnivå, inte efter maxeffekt.",
      "Redan Truma-kanaler i bilen? Då är Combi D nästan alltid billigast totalt.",
      "Alde är det enda alternativet som ger golvvärme och motorförvärmning.",
      "Undvik odokumenterade billigvärmare — försäkringsfrågan ensam avgör.",
    ],
  },
  {
    h2: "Vad kostar värmen per dygn jämfört med alternativen",
    paragraphs: [
      "Frågan folk egentligen ställer är inte vad värmaren kostar att köpa, utan vad det kostar att hålla 20 grader inne när det är kallt ute. Vi har räknat på fyra uppvärmningssätt vid −15 °C i en sju meter lång halvintegrerad med normal vinterisolering, allt med 2026 års priser: diesel 18 kr/l, gasol cirka 40 kr/kg och landström 2,50 kr/kWh på ställplats.",
      "Slutsatsen är att landström nästan alltid är billigast när den finns, men att skillnaden mellan diesel och gasol är liten i kronor. Det som avgör i praktiken är tillgänglighet: dieseln finns i tanken, gasolen måste bytas, och landström finns bara där någon dragit fram den.",
      "En kombination är det som fungerar bäst i verkligheten. Stå på landström när du kan, låt dieselvärmaren ta nätterna på fri ställplats och behåll gasolen till spisen. Då slipper du både tomma flaskor och tömd batteribank.",
    ],
    table: {
      caption: "Kostnad per dygn vid −15 °C, 20 °C inne, sju meters halvintegrerad",
      head: ["Uppvärmning", "Förbrukning/dygn", "Kostnad/dygn", "Elbehov"],
      rows: [
        ["Dieselvärmare 4 kW", "3,8 liter", "cirka 68 kr", "25–50 Ah"],
        ["Gasol, Truma Combi 6", "2,0 kg", "cirka 80 kr", "15–25 Ah"],
        ["Landström 2 kW el", "cirka 26 kWh", "cirka 65 kr", "0 Ah (laddar dessutom)"],
        ["Diesel + nattsänkning 16 °C", "3,0 liter", "cirka 54 kr", "20–40 Ah"],
      ],
    },
    bullets: [
      "Prisskillnaden diesel–gasol är liten; bekvämligheten skiljer stort.",
      "Nattsänkning till 16 °C sparade 22 procent i vår mätning.",
      "Landström värmer och laddar samtidigt — prioritera den när den finns.",
    ],
  },
  {
    h2: "Innan du bokar installation — checklista som sparar pengar",
    paragraphs: [
      "Hälften av prisspannet i en offert handlar inte om värmaren utan om hur mycket inredning som måste bort. Går bränsleledningen att dra genom befintligt uttag, finns det plats under en bänk nära ytterväggen och kan avgasröret hamna långt från fönster och luftintag? Svaren avgör om du landar i den nedre eller övre delen av spannet.",
      "Ta med registreringsbevis, uppgift om totalvikt, batterityp och kapacitet, samt bild på tankuttaget när du ber om offert. Med de uppgifterna kan vi ge fast pris i stället för ett spann, och du slipper tilläggsfakturor.",
      "Tänk också igenom var termostaten ska sitta. Sitter givaren i en luftström från värmaren mäter den fel och bilen pendlar mellan för varmt och för kallt hela natten. Vi placerar den i sovutrymmet i ögonhöjd, aldrig i hallen vid dörren.",
    ],
    bullets: [
      "Fråga alltid om avgasutgångens placering — det är säkerhetsfrågan, inte en detalj.",
      "Skicka batterityp och kapacitet med offertförfrågan; värmaren är sällan flaskhalsen.",
      "Placera termostatgivaren i sovutrymmet, inte i luftflödet.",
      "Begär dokumentation och CE-intyg — försäkringsbolaget kan kräva det efter brand.",
    ],
  },
];

export const dieselvarmareExtraFaq: HubFaq[] = [
  {
    q: "Hur lång är livslängden på en dieselvärmare?",
    a: "Med årlig service håller en Webasto eller Eberspächer normalt 3 000–5 000 driftstimmar, vilket för de flesta husbilsägare motsvarar 12–15 år. Utan service är det glödstift och brännkammare som sätter gränsen, ofta redan efter fem säsonger.",
  },
  {
    q: "Får jag installera dieselvärmaren själv?",
    a: "Det finns inget förbud, men avgasdragning, bränsleuttag och elmatning är säkerhetskritiska moment och en felplacerad avgasutgång kan leda in kolmonoxid i boendedelen. Många försäkringsbolag kräver dessutom dokumenterad installation vid brandskada, så vi avråder.",
  },
  {
    q: "Hur mycket låter en dieselvärmare på natten?",
    a: "En luftvärmare i låg effekt ligger på 42–48 dB inne i bilen, ungefär som en kylskåpskompressor, plus pumpens tickande som hörs tydligast utifrån. Vattenburna system som Alde ligger under 35 dB och är det enda alternativet om ljud är avgörande.",
  },
  {
    q: "Fungerar dieselvärmaren när bilen står på höjd eller lutar?",
    a: "Ja, upp till cirka 5–8 graders lutning enligt tillverkarna. Vid kraftigare lutning kan sugröret hamna över bränsleytan vid låg tanknivå, vilket ger luft i systemet och driftstopp — ännu ett skäl att inte köra under en fjärdedels tank vintertid.",
  },
  {
    q: "Kan jag köra dieselvärmaren under färd?",
    a: "Ja, samtliga vanliga modeller är godkända för drift under körning och det är dessutom bra för värmaren eftersom laddningen från generatorn täcker strömbehovet. Undvik bara att köra den vid tankning.",
  },
  {
    q: "Vad kostar årlig service på en dieselvärmare?",
    a: "1 200–2 000 kr hos oss i Kalix, vilket omfattar rengöring av brännkammare, kontroll eller byte av glödstift och sil, felkodsläsning och en funktionskontroll på full effekt. Boka i september–oktober, då är kön kortast.",
  },
];

/* ------------------------------ /husbilsdack ------------------------------ */

export const husbilsdackTldr: string[] = [
  "Kör CP- eller C-märkta däck med minst det lastindex registreringsbeviset anger — aldrig personbilsdäck i samma dimension.",
  "Byt på ålder: sex år i drift, senast tio år oavsett mönsterdjup. DOT-koden 2219 betyder vecka 22 år 2019.",
  "Sätt trycket per axel efter vägning: typiskt 4,5 bar fram och 5,5 bar bak på en fullastad halvintegrerad.",
  "Vinterdäck krävs 1 december–31 mars vid vinterväglag, även för husbil.",
  "En komplett omgång CP-däck monterad kostar 10 500–15 500 kr 2026.",
];

export const husbilsdackExtraSections: HubSection[] = [
  {
    h2: "Vad en omgång kostar — dimension för dimension",
    paragraphs: [
      "Priserna nedan är kompletta priser hos oss i Kalix: fyra däck, montering, balansering, nya ventiler och miljöavgift, inklusive moms. Har du dubbelmontage bak blir det sex däck och priset ökar i motsvarande grad.",
      "Skillnaden mellan premiumdäck och budgetalternativ ligger sällan i mönsterdjup utan i hur gummiblandningen åldras. Ett Michelin Agilis CamperConsumption eller Continental VanContact Camper håller normalt hela sexårsperioden utan sprickbildning, medan billigare fabrikat ofta är sprickiga i sidoväggens bottenspår redan efter fyra vintrar i norrländsk kyla. Räknat per år är premiumdäcket därför oftast billigast.",
      "Ett tips som sparar mest pengar av alla: byt en axel i taget om ekonomin kniper, och börja med framaxeln där styrning och bromsning avgörs. Blanda däremot aldrig fabrikat eller typer på samma axel.",
    ],
    table: {
      caption: "Komplett omgång CP-däck monterad, priser 2026",
      head: ["Dimension", "Vanlig på", "Budget", "Premium"],
      rows: [
        ["215/70 R15 CP", "Äldre husbilar, husvagn", "8 500 kr", "11 500 kr"],
        ["225/75 R16 CP", "Fiat Ducato, de flesta", "10 500 kr", "14 000 kr"],
        ["235/65 R16 CP", "Tyngre integrerade", "11 500 kr", "15 500 kr"],
        ["195/75 R16 C", "Campervan", "7 500 kr", "10 500 kr"],
      ],
    },
    bullets: [
      "Priserna inkluderar montering, balansering, ventiler och miljöavgift.",
      "Premium håller normalt hela sexårsperioden utan sprickbildning.",
      "Byt hellre en hel axel i taget än ett enstaka däck.",
    ],
  },
  {
    h2: "Däcktryck efter axelvikt — tabell att räkna på",
    paragraphs: [
      "Tabellen visar de tryck vi sätter på 225/75 R16 CP efter vägd axelvikt. Väg bilen färdiglastad med full vattentank, gasol, cyklar och passagerare. Flera återvinningscentraler i Norrbotten väger för 50–100 kr, och en vägning räcker i flera år så länge du packar likadant.",
      "Ligger din vägda axelvikt mellan två rader väljer du alltid närmast högre tryck. För lågt tryck värmer däcket och bryter ned stommen inifrån, för högt tryck slits mitten och ger stötigare gång men skadar inte däcket på samma sätt. Säkerhetsmarginalen ska alltså gå uppåt.",
      "Inför vinterförvaring höjer du 0,5 bar över tabellen, eftersom trycket sjunker 0,3–0,4 bar av bara kylan och däcket annars står och plattar till sig under sin egen last.",
    ],
    table: {
      caption: "225/75 R16 CP — tryck per axel efter vägd vikt",
      head: ["Vägd axelvikt", "Tryck fram", "Tryck bak"],
      rows: [
        ["1 500 kg", "3,5 bar", "3,5 bar"],
        ["1 750 kg", "4,0 bar", "4,0 bar"],
        ["2 000 kg", "4,5 bar", "4,5 bar"],
        ["2 250 kg", "5,0 bar", "5,0 bar"],
        ["2 500 kg", "—", "5,5 bar"],
      ],
    },
    bullets: [
      "Mellan två rader: välj alltid närmast högre tryck.",
      "Mät kallt, minst en gång i månaden under säsong.",
      "Vinterförvaring: höj 0,5 bar och ställ bilen på plana plattor eller stödben.",
    ],
  },
  {
    h2: "Så gör du besiktningen till en formalitet",
    paragraphs: [
      "Däck är en av de vanligaste anmärkningarna på husbil vid besiktning, och nästan alla går att undvika hemma på fem minuter. Kontrollera fyra saker: att mönsterdjupet är minst 1,6 mm sommartid och 3 mm på vinterdäck vid vinterväglag, att inga sprickor når in i gummits stomme, att lastindex och hastighetsindex minst matchar registreringsbeviset och att samma typ sitter på samma axel.",
      "Titta också på ventilerna. Gummiventiler åldras precis som däck och är en vanlig läckagekälla på husbil, där de sitter under hög belastning från högt tryck. Vi byter alltid ventiler vid däckbyte, och det bör den verkstad du väljer också göra.",
      "Har du tryckövervakning (TPMS) eftermonterad, kontrollera att sensorerna verkligen svarar innan resan. Trasiga sensorer i sig är ingen anmärkning, men de invaggar dig i falsk trygghet — och en punktering bak på ett dubbelmontage kan du köra länge på utan att märka något utan fungerande övervakning.",
    ],
    bullets: [
      "Mönsterdjup: minst 1,6 mm sommar, 3 mm vinterdäck vid vinterväglag.",
      "Lastindex och hastighetsindex minst enligt registreringsbeviset.",
      "Byt gummiventiler vid varje däckbyte.",
      "Kontrollera att TPMS-sensorerna svarar innan långresan.",
    ],
  },
];

export const husbilsdackExtraFaq: HubFaq[] = [
  {
    q: "Vad kostar en omgång husbilsdäck 2026?",
    a: "En komplett omgång 225/75 R16 CP monterad kostar 10 500 kr i budgetklass och cirka 14 000 kr i premium hos oss i Kalix, inklusive montering, balansering, ventiler och miljöavgift. Mindre dimensioner ligger 2 000–3 000 kr lägre.",
  },
  {
    q: "Måste jag ha CP-märkta däck på husbil?",
    a: "Du måste ha däck med minst det lastindex registreringsbeviset anger, och i praktiken innebär det CP- eller C-märkta däck. CP-däcket har kraftigare sidovägg, tål tryck upp mot 5,5 bar och är byggt för att stå stilla fullastat utan att få permanenta plattor.",
  },
  {
    q: "Får jag köra dubbdäck på husbil?",
    a: "Ja, dubbdäck är tillåtna 1 oktober–15 april och även utanför den perioden vid vinterväglag. På tunga husbilar ger dubb mest på isiga backar, medan ett bra odubbat friktionsdäck ofta är tystare och fullt tillräckligt söder om Gävle.",
  },
  {
    q: "Hur förvarar jag husbilen så däcken inte blir platta?",
    a: "Höj trycket 0,5 bar över lasttabellen, ställ bilen på plana underlägg eller avlasta på stödben, undvik direkt solljus och flytta bilen någon meter en gång under vintern. Platta fläckar som inte försvinner efter tio minuters körning betyder att stommen tagit skada.",
  },
  {
    q: "Kan jag byta till en annan däckdimension?",
    a: "Bara om den nya dimensionen finns angiven i registreringsbeviset eller om du har intyg från tillverkaren. Annars påverkas hastighetsmätare, lastförmåga och i vissa fall ABS-systemets beräkningar, och bilen kan underkännas vid besiktning.",
  },
  {
    q: "Hur ofta ska jag kontrollera däcktrycket på husbil?",
    a: "Minst en gång i månaden under säsong och alltid inför en längre resa, alltid kallt. Ett husbilsdäck tappar naturligt 0,1–0,2 bar i månaden, och vid vinterförvaring i kyla ytterligare 0,3–0,4 bar.",
  },
];

/* ------------------------------ /salja-husbil ----------------------------- */

export const saljaHusbilTldr: string[] = [
  "Inbyte ger normalt 10–20 procent under marknadspris men pengar samma dag; förmedling kostar 5–9 procent i avgift men ger högre slutpris.",
  "Privatförsäljning ger mest i handen men tar i snitt 6–14 veckor och kräver att du hanterar visningar, provkörning och betalning själv.",
  "Starkaste säljfönstret är mars–maj, näst bäst augusti. En annons i november kostar dig ofta 20 000–40 000 kr i pris.",
  "Ett fukttestprotokoll för 1 500–2 500 kr kortar säljtiden mer än en prissänkning gör.",
  "Värdera på faktiska slutpriser för samma modellår och planlösning — inte på utropspriser i annonser.",
];

export const saljaHusbilExtraSections: HubSection[] = [
  {
    h2: "Tre säljvägar — vad du får kvar efter avdrag",
    paragraphs: [
      "Räkneexemplet nedan utgår från en husbil med marknadsvärde 650 000 kr, och visar vad de tre vägarna typiskt ger netto i handen. Siffrorna bygger på affärer vi själva gjort eller förmedlat de senaste två åren.",
      "Notera att inbytet inte är så dåligt som mellanskillnaden ser ut. Byter du in mot en dyrare bil betalar du bara moms på mellanskillnaden hos en handlare, och du slipper både dubbla ägandekostnader och risken att stå med två fordon över en vinter. Räknat över hela affären brukar skillnaden mot förmedling krympa till några tiotusen.",
      "Privatförsäljning ger mest på pappret men kostar tid, och den tiden har ett pris: försäkring, skatt, uppställningsplats och värdeminskning fortsätter medan bilen står osåld. En bil som står osåld i fyra månader tappar i normalfallet 15 000–25 000 kr i värde utöver de löpande kostnaderna.",
    ],
    table: {
      caption: "Marknadsvärde 650 000 kr — vad varje väg ger netto",
      head: ["Säljväg", "Slutpris", "Avdrag", "Netto", "Tid"],
      rows: [
        ["Inbyte hos handlare", "550 000 kr", "0 kr", "550 000 kr", "Samma dag"],
        ["Förmedling", "640 000 kr", "cirka 45 000 kr", "595 000 kr", "3–8 veckor"],
        ["Privat, egen annons", "650 000 kr", "cirka 8 000 kr", "642 000 kr", "6–14 veckor"],
      ],
    },
    bullets: [
      "Inbyte: tryggast och snabbast, lägst pris men momsfördel vid byte uppåt.",
      "Förmedling: du behåller ägandet, handlaren sköter visning och papper.",
      "Privat: högst netto, men du bär hela ansvaret för besiktning, betalning och köparens invändningar.",
    ],
  },
  {
    h2: "Vad som faktiskt höjer och sänker priset",
    paragraphs: [
      "Vi värderar hundratals fritidsfordon per år, och avdragen är förvånansvärt förutsägbara. Fukt är den enskilt största posten och den enda som kan göra bilen praktiskt taget osäljbar. Däckens ålder, servicehistorik och antalet nycklar är sådant köpare räknar av direkt i kronor.",
      "Uppåt drar färre saker än folk tror. Extrautrustning ger sällan tillbaka mer än en tredjedel av inköpspriset, med två undantag: solceller med litiumbatteri och dragkrok, som båda är efterfrågade nog att i princip räknas som prishöjande. En nymålad eller professionellt polerad kaross ger däremot oproportionerligt mycket — inte i värdering, men i hur snabbt bilen säljs.",
      "Det som säljer allra snabbast är ett fullständigt underlag: fukttestprotokoll som inte är äldre än sex månader, servicebok, gasolbesiktning, alla nycklar och tjugo bra bilder i dagsljus. Vi ser regelmässigt att bilar med det materialet säljs på halva tiden mot likvärdiga bilar utan.",
    ],
    table: {
      caption: "Typiska avdrag och tillägg vid värdering",
      head: ["Post", "Effekt på pris"],
      rows: [
        ["Fuktvärde 15–20 % i vägg", "−25 000 till −60 000 kr"],
        ["Fuktvärde över 20 %", "−80 000 kr eller osäljbar utan åtgärd"],
        ["Däck äldre än sex år", "−12 000 kr"],
        ["Saknad servicehistorik", "−15 000 till −30 000 kr"],
        ["Solceller + litium", "+15 000 till +25 000 kr"],
        ["Dragkrok", "+8 000 till +12 000 kr"],
        ["Färskt fukttestprotokoll", "Kortar säljtiden 30–50 procent"],
      ],
    },
  },
  {
    h2: "Papperen och betalningen — så gör du affären trygg",
    paragraphs: [
      "Vid privatförsäljning är det två moment som går fel: ägarbytet och betalningen. Gör ägarbytet digitalt hos Transportstyrelsen medan köparen står bredvid, så ser ni båda att det gick igenom. Skriv ett köpekontrakt i två exemplar med registreringsnummer, mätarställning, pris, datum och en tydlig skrivning om att fordonet säljs i befintligt skick.",
      "Ta aldrig emot betalning på annat sätt än att pengarna syns på ditt konto innan nycklarna lämnas över. Swish har beloppstak, banköverföring mellan olika banker kan ta en bankdag, och kvitton på insättning går att förfalska. Vänta tills saldot är uppdaterat i din egen bankapp — inget annat räcker.",
      "Bilar som sålts som fritidsfordon till privatperson omfattas av köplagen, inte konsumentköplagen, vilket ger dig som säljare ett svagare men inte obefintligt skydd. Beskriv därför alla kända fel skriftligt i kontraktet. Ett känt och nedskrivet fel är sällan en tvist; ett förtiget fel blir det nästan alltid.",
    ],
    bullets: [
      "Genomför ägarbytet digitalt med köparen närvarande.",
      "Köpekontrakt i två exemplar, med kända fel utskrivna.",
      "Lämna aldrig nycklar innan pengarna syns på ditt eget konto.",
      "Behåll kopia på annonsen och kontraktet i minst två år.",
    ],
  },
];

export const saljaHusbilExtraFaq: HubFaq[] = [
  {
    q: "Vad är skillnaden mellan förmedling och inbyte?",
    a: "Vid inbyte köper handlaren bilen direkt, ofta 10–20 procent under marknadspris, och du får pengarna eller avräkningen samma dag. Vid förmedling behåller du ägandet medan handlaren annonserar, visar och sköter papperen mot en avgift på 5–9 procent — högre slutpris men längre tid.",
  },
  {
    q: "Hur lång tid tar det att sälja en husbil?",
    a: "Inbyte går samma dag, förmedling tar normalt 3–8 veckor och privatförsäljning 6–14 veckor. Säsongen påverkar mest: en välprissatt bil i mars säljs ofta på under en månad, medan samma bil i november kan stå kvar hela vintern.",
  },
  {
    q: "När på året är det bäst att sälja husbil?",
    a: "Mars till maj är starkast, med augusti som näst bästa fönster. Annonserar du i november–januari får du färre spekulanter och pressas ofta 20 000–40 000 kr i pris jämfört med vårförsäljning.",
  },
  {
    q: "Varför säljs inte min husbil?",
    a: "I nio fall av tio är det priset i förhållande till bilder och underlag, inte bilen. Har annonsen legat fyra veckor utan visningsbokning ligger du för högt. Komplettera med fukttestprotokoll, tjugo bilder i dagsljus och en beskrivning av kända fel innan du sänker priset — det brukar ge effekt först.",
  },
  {
    q: "Behöver jag göra fukttest innan försäljning?",
    a: "Det är inget krav, men ett protokoll som inte är äldre än sex månader kostar 1 500–2 500 kr och kortar säljtiden med 30–50 procent i vår erfarenhet. Det tar bort köparens största osäkerhet och stoppar prutförsök som annars landar på tiotusentals kronor.",
  },
  {
    q: "Måste jag betala skatt när jag säljer min husbil?",
    a: "Nej, försäljning av personligt lösöre som en husbil är normalt inte skattepliktig för privatpersoner så länge du inte gör vinst på ett fordon du köpt i spekulationssyfte eller bedriver handel. Säljer du regelbundet flera fordon per år kan Skatteverket däremot bedöma det som näringsverksamhet.",
  },
];

/* ----------------------------- /tvatta-husvagn ---------------------------- */

export const tvattaHusvagnTldr: string[] = [
  "Tvätta i skugga på sval yta, uppifrån och ner, i sektioner om cirka två kvadratmeter — aldrig i solsken.",
  "Använd pH-neutralt husvagnsschampo. Bilschampo med avfettning bryter ned tätningsmassan i skarvarna.",
  "Håll högtryck minst 40 cm från ytan och rikta det aldrig mot lister, luckor eller takgenomföringar.",
  "Svartstrimmor kräver ett dedikerat black streak remover — vanligt schampo tar dem inte.",
  "Rätt månad att tvätta är oktober, före förvaringen. En komplett tvätt med polering och vax kostar 3 500–6 000 kr hos oss.",
];

export const tvattaHusvagnExtraSections: HubSection[] = [
  {
    h2: "Rätt medel till rätt yta",
    paragraphs: [
      "Husvagnen består av fyra material som reagerar helt olika på kemi: lackerad eller gelcoatad plast, blank aluminiumplåt, akrylfönster och gummilister. Ett medel som är perfekt på det ena kan förstöra det andra permanent, och det är där de flesta skadorna uppstår.",
      "Akrylfönster är det känsligaste. Ett enda torkat papper eller en droppe lösningsmedelsbaserat medel ger mikrorepor som inte går att polera bort utan att fönstret blir matt. Använd bara rikligt med vatten, mjuk mikrofiber och akrylrengöring — aldrig glasrengöring med ammoniak.",
      "Gummilister vill ha silikonfritt gummivårdsmedel. Silikon ger snygg glans en säsong men torkar ut gummit på sikt och gör dessutom att framtida tätningsmassa inte fäster, vilket blir dyrt den dag en list ska bytas.",
    ],
    table: {
      caption: "Material, medel och det du ska undvika",
      head: ["Yta", "Använd", "Undvik"],
      rows: [
        ["Gelcoat / lackad plast", "pH-neutralt husvagnsschampo", "Avfettning, starkt alkaliskt"],
        ["Aluminiumplåt", "Milt alkaliskt, kort verkningstid", "Sura fälgrengöringsmedel"],
        ["Akrylfönster", "Akrylrengöring + mikrofiber", "Ammoniak, torrtorkning, papper"],
        ["Gummilister", "Silikonfritt gummivårdsmedel", "Silikonspray, vaselin"],
        ["Tak och genomföringar", "Mjuk borste, lågt tryck", "Högtryck närmare än 40 cm"],
      ],
    },
  },
  {
    h2: "Svartstrimmor, oxidation och det grå — tre olika problem",
    paragraphs: [
      "De svarta ränderna under lister och fönster är inte smuts utan en blandning av tätningsmassans mjukgörare och luftburna partiklar som bränts fast av UV. Vanligt schampo tar dem aldrig. Du behöver ett dedikerat black streak remover, som du applicerar på sval yta, låter verka två till tre minuter och torkar av med mikrofiber — inte skrubbar, eftersom ränderna sitter i ytan och inte ovanpå den.",
      "Den gråa, kritiga ytan som färgar av sig på handen är oxiderad gelcoat, alltså ett ytskikt som brutits ned av UV. Den försvinner bara med maskinpolering: skärande rondell med medelgrovt polermedel först, därefter finpolering och en spärrande vaxning eller keramisk behandling. Vi räknar sex till nio timmars arbete på en familjevagn och tar 3 500–6 000 kr för hela paketet.",
      "Grönalger och svart mögelbeläggning på norrsidan är det tredje problemet och det enklaste. Ett alkaliskt algmedel med tio minuters verkningstid, sedan skölj — ingen skrubbning alls. Skrubbar du repar du gelcoaten och gör ytan mer benägen att fästa alger nästa år, alltså precis tvärtom mot vad du vill.",
    ],
    bullets: [
      "Svartstrimmor: dedikerat medel, 2–3 minuters verkningstid, torka av — skrubba inte.",
      "Oxidation: maskinpolering i två steg plus spärrande vax eller keramik.",
      "Alger: alkaliskt medel och sköljning, aldrig borste.",
      "Efterbehandla alltid — en opolerad gelcoat oxiderar snabbare varje år.",
    ],
  },
  {
    h2: "Årsschema — när du gör vad",
    paragraphs: [
      "Vi får ofta frågan hur ofta en husvagn behöver tvättas. Svaret är två gånger om året om den står ute, en gång om den står inne, plus en snabbsköljning efter varje resa längs saltade vägar. Det viktiga är inte antalet tvättar utan tidpunkten: en vagn som ställs undan smutsig i oktober har alger och intorkade insekter fastbrända till våren, och då krävs polering i stället för tvätt.",
      "Vintertvätt är möjlig men ska göras i uppvärmd hall. Tvättar du utomhus vid minusgrader fryser vatten i lister och luckor, och en fastfrusen dörrlist som rivs upp är en av de vanligare skadorna vi lagar i mars. Kostnaden för att laga en list överstiger vad tvätten kostade.",
      "Behandlingens hållbarhet skiljer sig också. Ett vanligt vax håller ungefär en säsong, en polymerförsegling två, och en keramisk behandling tre till fyra år. Räknat på arbetstiden i norrländskt klimat brukar keramik gå jämnt ut redan efter tredje året, samtidigt som vagnen är märkbart lättare att tvätta hela tiden.",
    ],
    table: {
      caption: "Skötselschema för en husvagn som står ute",
      head: ["Tidpunkt", "Åtgärd", "Tid"],
      rows: [
        ["April, före säsong", "Tvätt, listvård, kontroll av tätningar", "2–3 h"],
        ["Efter varje resa", "Sköljning av front och underrede", "20 min"],
        ["Oktober, före förvaring", "Fullständig tvätt, svartstrimmor, vax", "4–6 h"],
        ["Vart tredje år", "Maskinpolering + keramisk behandling", "6–9 h"],
      ],
    },
  },
];

export const tvattaHusvagnExtraFaq: HubFaq[] = [
  {
    q: "Får man tvätta husvagn med högtryckstvätt?",
    a: "Ja, men bara med bred stråle, minst 40 cm avstånd och aldrig riktat mot lister, luckor, takgenomföringar eller dekaler. Högtryck på nära håll pressar in vatten bakom tätningarna och är en av de vanligaste orsakerna till fuktskador vi hittar i innertak.",
  },
  {
    q: "Vad tar bort svarta ränder på husvagnen?",
    a: "Ett dedikerat black streak remover applicerat på sval yta med två till tre minuters verkningstid, sedan avtorkning med mikrofiber. Vanligt schampo och avfettning fungerar inte, eftersom ränderna är mjukgörare från tätningsmassan som bränts fast i ytan.",
  },
  {
    q: "Kan jag köra husvagnen genom en automattvätt?",
    a: "Nej. Borstarna är byggda för lastbilars plana sidor och river i lister, markiser, lampor och takdetaljer, och höjden räcker sällan. Handtvätt eller en verkstad som tvättar fritidsfordon är enda alternativet.",
  },
  {
    q: "Vad kostar det att få husvagnen tvättad och polerad?",
    a: "Hos oss i Kalix kostar en fullständig tvätt med svartstrimmebehandling, polering och vax 3 500–6 000 kr beroende på storlek och hur oxiderad ytan är. Enbart utvändig tvätt ligger på 1 200–1 800 kr.",
  },
  {
    q: "Hur ofta ska en husvagn tvättas?",
    a: "Två gånger per år om den står ute, en gång om den står inne, plus en snabbsköljning efter varje resa på saltade vägar. Viktigast är tvätten i oktober före förvaring — smuts som får sitta över vintern kräver polering i stället för tvätt på våren.",
  },
  {
    q: "Är keramisk behandling värt pengarna på husvagn?",
    a: "På en vagn som står ute i norrländskt klimat, ja. En keramisk behandling håller tre till fyra år mot vaxets en säsong, gör tvätten snabbare varje gång och bromsar oxidationen av gelcoaten. Räknat på arbetstid brukar den gå jämnt ut redan tredje året.",
  },
];
